// Component: OrderDetailDrawer.jsx
// Purpose: Side drawer showing DeeTrade order lifecycle + broker cross-check from a page-level snapshot (R104.6).
//          Instrument-led header with PAPER/LIVE + side/status/source/risk badges; date+time IST on every
//          timestamp; DeeTrade-vs-broker field diff. Data scope per algo-trading SME review (build-now fields).
//          104 D9 — when the selected row is a rejected signal (order._isRejection === true, set by
//          OrdersTable's rejection-row normalization) renders the classified rejection detail instead
//          of the standard order lifecycle body (RejectionDetail, below).
//          DS-realign (#10474 spike) — surfaces now mirror the sibling tables' conventions
//          (border-[var(--divider)] + bg-bg-2 on a bg-bg-1 canvas; DS type scale) instead of the
//          drifted local styling, and the compact timeline is promoted to a full vertical
//          LifecycleTimeline section fed by GET /orders/{id}/timeline (processed_events ledger +
//          column stamps, server-merged); the #118 column spine remains the pre-fetch fallback.

import React, { useEffect, useCallback } from 'react';
import { EXIT_REASON_MAP, LEG_TYPE_DISPLAY, formatExitConversion, getSourceTagBadge, getPriceSourceBadge, _buildOrderTimeline, describeOrderCharges } from '../../utils/tradingTransforms';
import { rejectReasonLabel, fullRejectReason, failureTypeLabel, originLabel, HISTORICAL_INCOMPLETE_LABEL } from '../../utils/rejectReason';
import { Badge, Button } from '../ds';
import ReconcileBadge from './ReconcileBadge';
import StatusBadge from './StatusBadge';

/*>>> HELPERS <<<*/

//! Exchange-facing timestamps show DATE + HH:MM:SS in IST (Asia/Kolkata) with the zone labelled —
//  a bare time is unauditable on NSE/BSE (you can't tell which trading day). Internal logic stays UTC.
const _dateTimeIST = new Intl.DateTimeFormat('en-IN', {
  timeZone: 'Asia/Kolkata',
  day: '2-digit', month: 'short', year: 'numeric',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
});

const formatDateTimeIST = (val) => {
  if (val == null) return null;
  try {
    const d = typeof val === 'number' ? new Date(val) : new Date(val);
    if (isNaN(d.getTime())) return null;
    return `${_dateTimeIST.format(d)} IST`;
  } catch {
    return null;
  }
};

const formatPrice = (val) => {
  if (val == null) return '—';
  const num = typeof val === 'number' ? val : parseFloat(val);
  if (isNaN(num)) return '—';
  return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const _num = (v) => {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return isNaN(n) ? null : n;
};

// --> R4.2/R4.3 — render a _computeTatLatency result as a human duration, or '—' when unavailable.
//     Never shows 0ms/negative/multi-decade (the guard collapses those to { unavailable: true }).
const _fmtLatency = (res) => {
  if (!res || res.unavailable || res.ms == null) return '—';
  const ms = res.ms;
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} s`;
};

// --> en-IN rupee value (lakh grouping); used for traded value
const formatRupees = (val) => {
  const n = _num(val);
  if (n == null) return '—';
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

/*>>> SHARED SURFACE PRIMITIVES <<<*/
// Mirror the sibling tables (OrdersTable/PositionsTable): a card is bg-bg-2 with a real
// var(--divider) border on the bg-bg-1 drawer canvas — NOT the old no-op `border-divider`.

const SURFACE = 'rounded-xl border border-[var(--divider)] bg-bg-2';

// --> Key/value field (label above value). Used in 2-col grids for scannable density.
const Field = ({ label, children, mono = false, full = false }) => (
  <div className={full ? 'col-span-2' : undefined}>
    <dt className="ds-eyebrow">{label}</dt>
    <dd className={`mt-1 text-[13px] leading-snug text-fg-1 ${mono ? 'font-mono tabular-nums' : ''}`}>
      {children == null || children === '' ? '—' : children}
    </dd>
  </div>
);

// --> Section header with optional right-aligned aside (e.g. "as of …")
const SectionHead = ({ title, aside }) => (
  <div className="mb-2.5 flex items-baseline justify-between gap-3">
    <h3 className="ds-eyebrow">{title}</h3>
    {aside ? <span className="text-[11px] tabular-nums text-fg-3">{aside}</span> : null}
  </div>
);

const Card = ({ children, className = '' }) => (
  <div className={`${SURFACE} p-4 ${className}`}>{children}</div>
);

// --> Slippage: 0 = clean (profit), positive = adverse (warning), negative = favourable (profit)
const Slippage = ({ value }) => {
  const n = _num(value);
  if (n == null) return <span className="text-fg-2">—</span>;
  if (n === 0) return <span className="font-mono tabular-nums text-profit">0.00</span>;
  if (n > 0) return <span className="font-mono tabular-nums text-warning">+{n.toFixed(2)}</span>;
  return <span className="font-mono tabular-nums text-profit">{n.toFixed(2)}</span>;
};

//! R101.4 — Pnl: never render ₹0.00 when source is null. grossLabel=true adds
//  "(gross, charges excluded)" sub-label when brokerage is null — we MUST NOT
//  present gross broker P&L as net P&L silently (R101.4 AC(c)).
//! MED-4 — grossLabel shows a NON-net qualifier. `indicative=true` (ICICI charges present but
//  pre-contract-note) says "(indicative, pre-contract-note)"; otherwise (brokerage null) it keeps
//  the original "(gross, charges excluded)". The P&L must never present as bare, unqualified "net"
//  off charges the adjacent Brokerage field labels indicative.
const Pnl = ({ value, grossLabel = false, indicative = false }) => {
  const n = _num(value);
  if (n == null) return <span className="text-fg-2">—</span>;
  const cls = n > 0 ? 'text-profit' : n < 0 ? 'text-loss' : 'text-fg-2';
  return (
    <span className="flex flex-col items-start gap-0.5">
      <span className={`font-mono tabular-nums font-semibold ${cls}`}>{n >= 0 ? '+' : ''}{n.toFixed(2)}</span>
      {grossLabel && (
        <span className="text-[10px] text-fg-2">
          {indicative ? '(indicative, pre-contract-note)' : '(gross, charges excluded)'}
        </span>
      )}
    </span>
  );
};

//! R101.4 — formatCharges: null/undefined → "–" or "Charges unavailable"; 0 is valid
//  (broker explicitly returned 0). NEVER render ₹0.00 when source is null.
const formatCharges = (val) => {
  if (val === null || val === undefined) return null; // caller renders "Charges unavailable"
  const n = _num(val);
  if (n == null) return null;
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

//--> 144-6 R7-T3 — the explicit "unavailable" state: names the reason when the API gave one.
const ChargesUnavailable = ({ view }) => (
  <span className="text-fg-2 italic" title={view?.reason || undefined}>
    Charges unavailable{view?.reasonPhrase ? ` (${view.reasonPhrase})` : ''}
  </span>
);

//--> Non-broker components, itemised (display only - no browser sums). Used when the aggregate
//    `taxes_and_charges` string is absent but the OMS breakdown is present (OMS-direct payloads).
const derivedTaxItems = (order) => (
  (Array.isArray(order?.charges_breakdown) ? order.charges_breakdown : [])
    .filter((c) => c && c.kind !== 'broker' && c.name !== 'brokerage' && c.name !== 'platform_fee'
      && formatCharges(c.amount) != null)
);

//! 144-6 R8 - per-component provenance: statutory lines name their circular (link), broker lines their
//  source URL (link); every line carries its verification badge and effective date.
const ChargesBreakdown = ({ order, view }) => {
  const rows = Array.isArray(order.charges_breakdown) ? order.charges_breakdown : [];
  const hasRows = rows.length > 0 && view.state === 'amount';
  const sourceLabel = view.source === 'broker_truth'
    ? 'Broker-reported'
    : (view.source === 'schedule' ? 'Schedule-derived' : null);
  return (
    <div className="mt-3" data-testid="charges-breakdown">
      <h4 className="ds-eyebrow mb-2">Charges breakdown</h4>
      <div className={`overflow-x-auto ${SURFACE}`}>
        {hasRows ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--divider)]">
                {['Component', 'Amount', 'Basis / rate', 'Source', 'Effective from', 'Status'].map((h) => (
                  <th key={h} className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-fg-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((c, idx) => {
                const link = c.circular_url || c.source_url || null;
                const label = c.circular_id || c.source_url || (c.kind === 'broker_reported' ? 'Broker-reported' : '—');
                return (
                  <tr key={`${c.name}-${idx}`} className="border-b border-[var(--divider)] last:border-0">
                    <td className="px-3 py-2 text-xs text-fg-1">{c.name}</td>
                    <td className="px-3 py-2 font-mono text-xs tabular-nums text-fg-1">{formatCharges(c.amount) ?? '—'}</td>
                    <td className="px-3 py-2 text-xs text-fg-2">
                      {c.rate != null ? `${c.rate} · ${c.basis || ''}` : (c.basis || '—')}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {link
                        ? <a href={link} target="_blank" rel="noopener noreferrer" className="text-accent underline">{label}</a>
                        : <span className="text-fg-2">{label}</span>}
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] tabular-nums text-fg-2">{c.effective_from ?? '—'}</td>
                    <td className="px-3 py-2 text-xs">
                      {c.verification_status === 'unverified' && <Badge variant="warn" size="sm">unverified</Badge>}
                      {c.verification_status === 'verified' && <Badge variant="profit" size="sm">verified</Badge>}
                      {!c.verification_status && <span className="text-fg-3">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className="px-3 py-3 text-xs text-fg-2" data-testid="charges-breakdown-unavailable">
            Charges unavailable{view.reasonPhrase ? ` (${view.reasonPhrase})` : ''}
            {view.reason ? <span className="ml-2 font-mono text-[10px] text-fg-3">{view.reason}</span> : null}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2 border-t border-[var(--divider)] px-3 py-2 text-[11px] text-fg-2">
          {view.state === 'amount' && (
            <span className="font-mono tabular-nums text-fg-1" data-testid="charges-breakdown-total">
              Total {formatCharges(view.total)}
            </span>
          )}
          {sourceLabel && <span>{sourceLabel}</span>}
          {view.indicative && <Badge variant="info" size="sm">Indicative</Badge>}
          {view.extrapolated && <Badge variant="warn" size="sm">Schedule extrapolated</Badge>}
          {view.partial && <span>Broker figures incomplete - schedule-derived charges shown</span>}
          {order.broker_truth_lookup_failed === true && (
            <span data-testid="charges-lookup-failed">Broker charges lookup failed - schedule shown</span>
          )}
          {order.broker_key && (
            <span>Broker {order.broker_key}{order.broker_resolution ? ` (${String(order.broker_resolution).replaceAll('_', ' ')})` : ''}</span>
          )}
        </div>
      </div>
    </div>
  );
};

/*>>> LIFECYCLE TIMELINE (#10474) <<<*/

// Dot styling per event kind. Every colour is a real DS token (accent/primary/profit/loss/
// warning/fg-*), so the rail reads correctly in both themes.
const TL_DOT = {
  milestone:       'bg-fg-3 border-fg-3',      // pre-broker internal stamp (signal/admitted/…)
  accepted:        'bg-fg-2 border-fg-2',      // OMS accepted (CREATED)
  ack:             'bg-accent border-accent',  // broker acknowledged (ROUTING_DECIDED)
  amend:           'bg-primary border-primary',// order amended
  'amend-rej':     'bg-warning border-warning',// broker rejected an amend
  broker:          'bg-bg-2 border-primary',   // hollow ring — broker status update (OPEN/partial)
  'terminal-fill': 'bg-profit border-profit',  // terminal — filled
  'terminal-cxl':  'bg-fg-2 border-fg-2',      // terminal — cancelled
  'terminal-rej':  'bg-loss border-loss',      // terminal — rejected
  // legacy kinds used by the null-events milestone-spine fallback (tests/loading):
  fill: 'bg-profit border-profit',
  cxl:  'bg-fg-2 border-fg-2',
  rej:  'bg-loss border-loss',
};

// event kind -> dynamic-legend entry [swatch classes, label]. Only kinds actually present in
// a given order's timeline are shown, so "Broker status" appears only when there ARE broker
// status updates, etc.
const TL_LEGEND = {
  milestone:       ['bg-fg-3', 'Internal (OMS)'],
  accepted:        ['bg-fg-2', 'Internal (OMS)'],
  ack:             ['bg-accent', 'Broker ack'],
  amend:           ['bg-primary', 'Amended'],
  'amend-rej':     ['bg-warning', 'Amend rejected'],
  broker:          ['bg-bg-2 border-2 border-primary', 'Broker status'],
  'terminal-fill': ['bg-profit', 'Filled'],
  'terminal-cxl':  ['bg-fg-2', 'Cancelled'],
  'terminal-rej':  ['bg-loss', 'Rejected'],
};

// Kinds that state a broker-side fact; on a PAPER order these are simulated, so the row is
// chip-qualified rather than letting "Broker acknowledged" read as a real broker claim.
const TL_BROKER_KINDS = new Set(['ack', 'broker', 'amend', 'amend-rej', 'terminal-fill', 'terminal-cxl', 'terminal-rej']);

// --> 10509-21 C17 — the persisted source label is the only attribution authority.
const TIME_SOURCE_LABEL = {
  exchange: 'Exchange time',
  broker: 'Broker time',
  detected: 'Detected',
  reconciled: 'Reconciled',
};

// Collapse exact adjacent duplicates (same event_type, same label) — e.g. a twice-delivered
// CANCELLED — into one row with a xN chip. Never merges different kinds or non-adjacent rows.
const _collapseAdjacentDuplicates = (events) => {
  const out = [];
  events.forEach((e) => {
    const prev = out[out.length - 1];
    if (prev && e.event_type && prev.event_type === e.event_type && prev.label === e.label) {
      prev.repeat = (prev.repeat || 1) + 1;
    } else {
      out.push({ ...e });
    }
  });
  return out;
};

// Latency between two ISO timestamps, humanised; null when unavailable/implausible.
const _evLatency = (prevIso, iso) => {
  if (!prevIso || !iso) return null;
  const ms = new Date(iso).getTime() - new Date(prevIso).getTime();
  if (isNaN(ms) || ms < 0 || ms > 1000 * 60 * 60 * 24 * 3650) return null; // negative / >10yr
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} s`;
};

// One timeline row. `latency` is its OWN text node so tests that match the exact duration string
// ('100 ms') keep passing. In the events path it is the gap to the previous event; for
// processed_events rows that is OMS processing time, not broker wall-clock.
const TimelineRow = ({ kind, label, timestamp, timeSource, chip, chipVariant, latency, latencyObserved, last }) => (
  <li className="relative grid grid-cols-[16px_1fr_auto] gap-x-3 pb-4 last:pb-0">
    <div className="relative flex justify-center">
      {!last && (
        <span
          aria-hidden="true"
          className="absolute left-1/2 top-[15px] -bottom-1 w-px -translate-x-1/2 bg-[var(--divider)]"
        />
      )}
      <span
        aria-hidden="true"
        className={`relative z-[1] mt-[3px] h-[11px] w-[11px] rounded-full border-2 ${TL_DOT[kind] || TL_DOT.milestone}`}
      />
    </div>

    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[13px] font-semibold leading-tight text-fg-1">{label}</span>
        {chip && <Badge variant={chipVariant || 'info'} size="sm">{chip}</Badge>}
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-1.5">
        <span className="ds-mono text-[11px] text-fg-2">{timestamp || '—'}</span>
        {timeSource && <Badge variant="info" size="sm">{timeSource}</Badge>}
      </div>
    </div>

    <div className="pt-[3px] text-right">
      {latency ? (
        <span
          className="inline-flex items-center gap-1 rounded-md bg-bg-3 px-2 py-0.5 text-[11px] tabular-nums text-fg-2"
          title={latencyObserved ? 'OMS-observed time (projector clock), not broker wall-clock' : undefined}
        >
          <span aria-hidden="true" className="text-fg-3">↑</span>
          <span>{latency}</span>
          {latencyObserved && <span className="text-fg-3">obs.</span>}
        </span>
      ) : null}
    </div>
  </li>
);

// LifecycleTimeline — the full vertical order lifecycle.
//   Primary source (#10474): the server-merged, time-ordered event log from
//     GET /orders/{id}/timeline — the processed_events ledger (OMS accepted, broker ack,
//     amend, broker status, partial, fill/cancel/reject) plus the order's pre-broker column
//     stamps where the producer wrote them. Latencies are computed between adjacent events.
//   Fallback (events === null, i.e. tests + the brief pre-fetch window): the #118 milestone
//     spine derived from the order's own columns via _buildOrderTimeline.
const LifecycleTimeline = ({ order, events, loading, error }) => {
  const hasEvents = Array.isArray(events);
  const isPaper = String(order.execution_mode ?? '').toUpperCase() === 'PAPER';

  // --- Production path: render the server's merged lifecycle event log ---
  if (hasEvents && events.length > 0) {
    const rows = _collapseAdjacentDuplicates(events);
    // Dynamic legend: one entry per distinct kind actually present (stable first-seen order).
    const legend = [];
    const seen = new Set();
    rows.forEach((e) => {
      const entry = TL_LEGEND[e.kind];
      if (entry && !seen.has(entry[1])) { seen.add(entry[1]); legend.push(entry); }
    });

    return (
      <div>
        <ol className="mt-1" aria-label="Order timeline">
          {rows.map((e, i) => {
            const prev = i > 0 ? rows[i - 1] : null;
            const paperChip = isPaper && TL_BROKER_KINDS.has(e.kind);
            return (
              <TimelineRow
                key={i}
                kind={e.kind}
                label={e.label}
                timestamp={formatDateTimeIST(e.at)}
                chip={e.repeat > 1
                  ? `×${e.repeat}`
                  : paperChip
                    ? 'Paper'
                    : TIME_SOURCE_LABEL[e.time_source]}
                chipVariant={e.repeat > 1 ? 'warning' : 'info'}
                /* #10479 D1 — latency keys on sort_at (always the OMS ledger clock), not `at`
                   (broker wall-clock for ROUTING_DECIDED/FILLED), so the ms<0 blanking below can
                   no longer fire on broker clock skew. latencyObserved still keys on `clock`. */
                latency={prev ? _evLatency(prev.sort_at, e.sort_at) : null}
                latencyObserved={!!prev && (prev.clock !== 'broker' || e.clock !== 'broker')}
                last={i === rows.length - 1}
              />
            );
          })}
        </ol>

        {legend.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 border-t border-[var(--divider)] pt-3">
            {legend.map(([cls, txt]) => (
              <span key={txt} className="inline-flex items-center gap-1.5 text-[11px] text-fg-2">
                <span aria-hidden="true" className={`h-2.5 w-2.5 rounded-full ${cls}`} />
                {txt}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  // --- Fetched but genuinely empty ---
  if (hasEvents && events.length === 0) {
    return (
      <p className="py-1 text-[12px] italic text-fg-3">No lifecycle events recorded for this order.</p>
    );
  }

  // --- Fallback (events === null): #118 milestone spine from the order's own columns ---
  const { points, hops } = _buildOrderTimeline(order);
  const status = String(order.status || '').toUpperCase();
  const terminalKind = status === 'REJECTED' ? 'rej' : status === 'CANCELLED' ? 'cxl' : 'fill';
  const kindFor = (key) =>
    key === 'routed_at' ? 'ack'
    : key === 'exchange_fill_timestamp' ? terminalKind
    : 'milestone';

  return (
    <div>
      <ol className="mt-1" aria-label="Order timeline">
        {points.map((point, index) => (
          <TimelineRow
            key={index}
            kind={kindFor(point.key)}
            label={point.label}
            timestamp={formatDateTimeIST(point.value)}
            timeSource={point.source ? TIME_SOURCE_LABEL[point.source] : null}
            // hop i is point i -> point i+1; its own text node so tests match '100 ms' exactly.
            latency={index < hops.length ? _fmtLatency(hops[index].latency) : null}
            last={index === points.length - 1}
          />
        ))}
      </ol>
      {loading && (
        <p className="mt-1 pl-7 text-[11px] italic text-fg-3">Loading lifecycle…</p>
      )}
      {error && !loading && (
        <p className="mt-1 pl-7 text-[11px] italic text-warning" role="status">
          Lifecycle events unavailable — showing the order's own timestamps only.
        </p>
      )}
    </div>
  );
};

// --> 104 D9 — Rejection detail panel. Rendered instead of the standard order lifecycle body when
//   the selected row never became an order (order._isRejection === true). Surfaces the FULL
//   classified detail: failure_type, origin, account, reject_reason, sub_reason, instrument/
//   side/qty/price when present, and the naked-position at-risk badge — reusing the SAME
//   Field/Card/SectionHead primitives and rejectReasonLabel/failureTypeLabel/originLabel
//   helpers as the rest of this drawer / OrdersTable (never a second divergent detail view).
const RejectionDetail = ({ order, onClose }) => {
  const reason = order.historical_incomplete === true
    ? HISTORICAL_INCOMPLETE_LABEL
    : rejectReasonLabel(fullRejectReason(order));
  const failureLabel = failureTypeLabel(order.failure_type);
  const origin = originLabel(order.origin);
  const acctLabel = order.account_label ?? order.account_id ?? '—';
  const placedLabel = formatDateTimeIST(order.created_at);

  return (
    <>
      <div
        className="fixed inset-x-0 bottom-0 top-[53px] z-40 bg-black/60 backdrop-blur-sm motion-safe:animate-[ds-fadeIn_140ms_ease-out]"
        aria-hidden="true"
        onClick={() => onClose?.()}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Rejection detail"
        className="fixed right-0 top-[53px] z-50 flex h-[calc(100dvh_-_53px)] w-full max-w-lg flex-col border-l border-[var(--divider)] bg-bg-1 shadow-xl motion-safe:animate-[ds-fadeIn_160ms_ease-out]"
      >
        <div className="border-b border-[var(--divider)] bg-bg-2 px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="ds-h1 truncate text-fg-1">Rejected signal</h2>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <StatusBadge status="REJECTED" size="sm" />
                {order.naked_position_active === true && (
                  <Badge
                    variant="loss"
                    size="sm"
                    title="Protective leg failed — this position may be naked. Manual intervention may be required."
                  >
                    AT RISK
                  </Badge>
                )}
              </div>
              <p className="ds-mono mt-2 text-[11px] text-fg-2">
                {placedLabel ? `Rejected ${placedLabel}` : 'Rejection time unavailable'}
              </p>
            </div>
            <button
              onClick={() => onClose?.()}
              className="shrink-0 rounded-md p-1.5 text-fg-2 transition-colors hover:bg-bg-3 hover:text-fg-1"
              aria-label="Close rejection detail"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto px-5 py-5">
          <section>
            <SectionHead title="Rejection" />
            <Card>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <Field label="Signal ID" mono full>{order.signal_id}</Field>
                <Field label="Account" full>{acctLabel}</Field>
                <Field label="Failure type">{failureLabel ?? '—'}</Field>
                <Field label="Origin">{origin ?? '—'}</Field>
                <Field label="Reject reason" full>{reason ?? '—'}</Field>
                {order.sub_reason && <Field label="Sub reason" full>{order.sub_reason}</Field>}
                <Field label="Execution mode">{order.execution_mode ?? '—'}</Field>
                {order.instrument_id && <Field label="Instrument" mono>{order.instrument_id}</Field>}
                {order.side && <Field label="Side">{order.side}</Field>}
                {order.qty != null && <Field label="Qty" mono>{order.qty}</Field>}
                {order.price != null && <Field label="Price" mono>{formatPrice(order.price)}</Field>}
                {order.lifecycle_id && <Field label="Lifecycle ID" mono full>{order.lifecycle_id}</Field>}
                {order.entry_stage && <Field label="Entry stage">{order.entry_stage}</Field>}
                {order.exit_kind && <Field label="Exit kind">{order.exit_kind}</Field>}
                {order.is_multi_stage === true && (
                  <Field label="Stage">
                    {order.target_stage != null && order.total_stages != null
                      ? `${order.target_stage} / ${order.total_stages}`
                      : '—'}
                  </Field>
                )}
                {order.matched === true && (
                  <Field label="Matched orders" full>
                    {Array.isArray(order.matched_orders) && order.matched_orders.length > 0
                      ? order.matched_orders.join(', ')
                      : '—'}
                  </Field>
                )}
              </dl>
            </Card>
          </section>
        </div>
      </aside>
    </>
  );
};

/*>>> MAIN COMPONENT <<<*/

/**
 * OrderDetailDrawer — side drawer with DeeTrade lifecycle + broker cross-check.
 *
 * R104.6 constraints:
 *   - Sourced ENTIRELY from the page-level snapshot prop (pageReconcileSnapshot).
 *   - MUST NOT trigger a fresh broker fetch / postReconcile on open.
 *   - Only the explicit "Refresh broker data" button calls onRefreshBroker.
 *   - Closes on Escape or backdrop click.
 */
const OrderDetailDrawer = ({
  open,
  onClose,
  order,
  pageReconcileSnapshot,
  onRefreshBroker,
  timelineEvents = null,
  timelineLoading = false,
  timelineError = false,
}) => {
  /*>>> EFFECTS <<<*/

  //! R104.6 — Escape key closes the drawer; no broker fetch happens here
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape' && open) {
        onClose?.();
      }
    },
    [open, onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // --> Lock body scroll when drawer is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  /*>>> DERIVED <<<*/

  //* Snapshot broker as_of display — DATE + time IST, from the page-level prop (not a fresh fetch)
  const brokerAsOfLabel = pageReconcileSnapshot?.as_of
    ? formatDateTimeIST(pageReconcileSnapshot.as_of)
    : null;

  const brokerRecord = pageReconcileSnapshot?.broker_record ?? null;
  const reconcileState = order?.reconcile_state ?? null;
  const fills = order?.fills ?? (order?.entryOrder ? [order.entryOrder] : []);

  /*>>> RENDER <<<*/
  if (!open || !order) return null;

  //! 104 D9 — a rejection row (OrdersTable._isRejection === true) never became an order and has
  //  none of the order-lifecycle fields this drawer otherwise reads — render its own classified
  //  detail view instead of falling through to the order-shaped body below.
  if (order._isRejection) {
    return <RejectionDetail order={order} onClose={onClose} />;
  }

  // instrument-id-display-remediation — display_symbol-first (stored canonical name); never a uuid.
  const symbol = order.display_symbol ?? order.symbol ?? order.instrument_symbol ?? order.name ?? null;
  const exchange = order.exchange ?? null;
  const mode = (order.execution_mode ?? '').toUpperCase();
  const side = order.side ?? null;
  const sourceBadge = order.source_tag ? getSourceTagBadge(order.source_tag) : null;
  const legLabel = formatExitConversion(order)
    ?? LEG_TYPE_DISPLAY[order.leg_type]
    ?? LEG_TYPE_DISPLAY[order.action]
    ?? order.action
    ?? order.signal
    ?? null;
  const placedLabel = formatDateTimeIST(order._epoch ?? order.created_at);

  const tradedValue = (() => {
    const fp = _num(order.filled_price ?? order.filledPrice);
    const q = _num(order.qty ?? order.quantity);
    const cm = _num(order.contract_multiplier) ?? 1;
    if (fp == null || q == null) return null;
    return fp * q * cm;
  })();

  //! 144-6 R7 - one reader of the per-order charge fields; schedule-derived charges read "indicative".
  const chargeView = describeOrderCharges(order);
  const chargeIndicative = order.charges_indicative === true || chargeView.indicative;

  // --> Broker diff rows (only when both records present); mismatch → loud loss colour
  const diffRows = brokerRecord ? [
    { label: 'Qty', dt: order.qty, bk: brokerRecord.qty },
    { label: 'Status', dt: order.status, bk: brokerRecord.status },
    { label: 'Limit price', dt: order.price ?? order.limit_price, bk: brokerRecord.price, price: true },
    { label: 'Filled', dt: order.filled_price ?? order.filledPrice, bk: brokerRecord.filled_price, price: true },
  ] : [];
  const mismatched = (r) => {
    if (r.dt == null || r.bk == null) return false;
    if (r.price) { const a = _num(r.dt), b = _num(r.bk); return a != null && b != null && Math.abs(a - b) > 0.005; }
    return String(r.dt).toUpperCase() !== String(r.bk).toUpperCase();
  };

  return (
    <>
      {/* Backdrop — click closes drawer */}
      <div
        className="fixed inset-x-0 bottom-0 top-[53px] z-40 bg-black/60 backdrop-blur-sm motion-safe:animate-[ds-fadeIn_140ms_ease-out]"
        aria-hidden="true"
        onClick={() => onClose?.()}
      />

      {/* Drawer panel — bg-bg-1 canvas so the bg-bg-2 cards read with proper contrast (same
          surface language as the OrdersTable this drawer opens from). */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Order detail"
        className="fixed right-0 top-[53px] z-50 flex h-[calc(100dvh_-_53px)] w-full max-w-lg flex-col border-l border-[var(--divider)] bg-bg-1 shadow-xl motion-safe:animate-[ds-fadeIn_160ms_ease-out]"
      >
        {/*>>> HEADER — instrument identity + safety badges (the load-bearing context) <<<*/}
        <div className="border-b border-[var(--divider)] bg-bg-2 px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-baseline gap-2">
                <h2 className="ds-h1 truncate text-fg-1">
                  {symbol ?? 'Order'}
                </h2>
                {exchange && <span className="shrink-0 text-xs font-medium text-fg-3">{exchange}</span>}
              </div>

              {/* badges: mode (paper/live) + side + status + source + risk */}
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {mode === 'LIVE' && <Badge variant="profit" size="sm">Live</Badge>}
                {mode === 'PAPER' && <Badge variant="info" size="sm">Paper</Badge>}
                {/* R8.4/R8.5 — server-derived 3-way label; never re-derive client-side */}
                {mode === 'UNATTRIBUTED' && <Badge variant="neutral" size="sm">Unattributed</Badge>}
                {side && <Badge variant={side === 'BUY' ? 'profit' : 'loss'} size="sm">{side}</Badge>}
                {order.status && <StatusBadge status={order.status} size="sm" />}
                {sourceBadge && <Badge variant={sourceBadge.variant} size="sm">{sourceBadge.label}</Badge>}
                {order.risk_flag === true && (
                  <Badge variant="warn" size="sm" title="Manual-vs-algo conflict detected">⚠ Conflict</Badge>
                )}
              </div>

              <p className="ds-mono mt-2 text-[11px] text-fg-2">
                {placedLabel ? `Placed ${placedLabel}` : 'Placement time unavailable'}
              </p>
            </div>

            <button
              onClick={() => onClose?.()}
              className="shrink-0 rounded-md p-1.5 text-fg-2 transition-colors hover:bg-bg-3 hover:text-fg-1"
              aria-label="Close order detail"
            >
              ✕
            </button>
          </div>
        </div>

        {/*>>> BODY <<<*/}
        <div className="flex-1 space-y-6 overflow-y-auto px-5 py-5">

          {/* -- Order (DeeTrade record) -- */}
          <section>
            <SectionHead title="Order" />
            <Card>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <Field label="Order ID" mono full>{order.order_id ?? order.orderId}</Field>
                <Field label="Action">{legLabel}</Field>
                <Field label="Type">{order.type ?? order.order_type}</Field>
                {/*! R1.3 — reject reason (broker's own human message) on the Order card, only for
                    REJECTED rows with a resolvable reason (never a fabricated "null"/"undefined"). */}
                {order.status === 'REJECTED' && rejectReasonLabel(fullRejectReason(order)) && (
                  <Field label="Reject reason" full>{rejectReasonLabel(fullRejectReason(order))}</Field>
                )}
                <Field label="Limit price" mono>{formatPrice(order.price ?? order.limit_price)}</Field>
                <Field label="Avg fill price" mono>{formatPrice(order.filled_price ?? order.filledPrice)}</Field>
                <Field label="Qty" mono>{order.qty ?? order.quantity}</Field>
                <Field label="Slippage"><Slippage value={order.slippage} /></Field>
                {order.instrument_lot_size != null && <Field label="Lot size" mono>{order.instrument_lot_size}</Field>}
                <Field label="Traded value" mono>{tradedValue == null ? '—' : formatRupees(tradedValue)}</Field>
                {/*! R101.4 — brokerage/taxes: null → "Charges unavailable", NOT ₹0.00
                    0.00 is valid only when the broker explicitly returned 0.
                    R6.2/#10505 A5 — some adapters' charges figures are pre-contract-note
                    estimates rather than the SEBI end-of-day contract note; the adapter
                    stamps `charges_indicative` (broker-neutral bool, P5) on the row and
                    we qualify "(indicative)" so the operator never reads them as final. */}
                <Field label="Brokerage" mono>
                  {(() => {
                    const cf = formatCharges(order.brokerage);
                    if (cf == null) return <ChargesUnavailable view={chargeView} />;
                    return chargeIndicative
                      ? <>{cf} <span className="text-fg-2 text-[10px]">(indicative)</span></>
                      : cf;
                  })()}
                </Field>
                <Field label="Taxes &amp; charges" mono>
                  {(() => {
                    const cf = formatCharges(order.taxes_and_charges);
                    if (cf == null) {
                      //--> 144-6 fallback: itemise the OMS breakdown's non-broker lines (no sums in the browser)
                      const items = derivedTaxItems(order);
                      if (items.length === 0) return <ChargesUnavailable view={chargeView} />;
                      return (
                        <>
                          {items.map((c) => `${c.name} ${formatCharges(c.amount)}`).join(' · ')}
                          {chargeIndicative && <span className="text-fg-2 text-[10px]"> (indicative)</span>}
                        </>
                      );
                    }
                    return chargeIndicative
                      ? <>{cf} <span className="text-fg-2 text-[10px]">(indicative)</span></>
                      : cf;
                  })()}
                </Field>
                <Field label="Strategy" full>{order.strategy ?? order.strategy_instance_id}</Field>
                <Field label="Placed" mono full>{placedLabel}</Field>
                {/* --> 10509-12 E13 / D14 — original SL values stay visible after the same
                    broker order row has become a LIMIT exit. All values come from the OMS
                    audit-derived `exit_conversion` object; no client-side reconstruction. */}
                {order.exit_conversion && (
                  <>
                    <Field label="Converted from" full>{formatExitConversion(order)}</Field>
                    <Field label="Prior order type">{order.exit_conversion.prior_order_type}</Field>
                    <Field label="Prior trigger" mono>{formatPrice(order.exit_conversion.prior_trigger_price)}</Field>
                    <Field label="Prior limit" mono>
                      {formatPrice(order.exit_conversion.prior_limit_price)}
                    </Field>
                    <Field label="Conversion reason">
                      {EXIT_REASON_MAP[order.exit_conversion.reason]
                        ?? LEG_TYPE_DISPLAY[order.exit_conversion.reason]
                        ?? 'Exit'}
                    </Field>
                    <Field label="Converted at" mono>
                      {formatDateTimeIST(order.exit_conversion.converted_at)}
                    </Field>
                  </>
                )}
              </dl>
            </Card>

            {/* 144-6 R7-T3 / R8 - derived charges breakdown with provenance + verification badges */}
            {(chargeView.state === 'amount' || chargeView.state === 'unavailable') && (
              <ChargesBreakdown order={order} view={chargeView} />
            )}

            {/* Fill / leg events */}
            {fills.length > 0 && (
              <div className="mt-3">
                <h4 className="ds-eyebrow mb-2">Fill / leg events</h4>
                <div className={`overflow-x-auto ${SURFACE}`}>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[var(--divider)]">
                        {['Leg', 'Side', 'Qty', 'Avg fill', 'P&L', 'Status', 'Time'].map((h) => (
                          <th
                            key={h}
                            className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-fg-3"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {fills.map((fill, idx) => {
                        const legName = LEG_TYPE_DISPLAY[fill.leg_type] ?? LEG_TYPE_DISPLAY[fill.action] ?? fill.leg_type ?? fill.action ?? 'Fill';
                        const ts = fill.bar_start || fill.created_at || fill.timestamp || fill._epoch;
                        return (
                          <tr key={fill.id ?? fill.order_id ?? idx} className="border-b border-[var(--divider)] last:border-0">
                            <td className="px-3 py-2 text-xs text-fg-1">{legName}</td>
                            <td className={`px-3 py-2 text-xs font-semibold ${fill.side === 'BUY' ? 'text-profit' : 'text-loss'}`}>
                              {fill.side ?? '—'}
                            </td>
                            <td className="px-3 py-2 font-mono text-xs tabular-nums text-fg-1">{fill.qty ?? '—'}</td>
                            <td className="px-3 py-2 font-mono text-xs tabular-nums text-fg-1">
                              {formatPrice(fill.filled_price ?? fill.filledPrice)}
                            </td>
                            {/*! R101.4 + MED-4 / #10505 A5 — P&L labeled non-net when brokerage is null
                                (gross) OR the row's charges are adapter-indicative (pre-contract-note),
                                per the broker-neutral `charges_indicative` flag (P5, no broker-name branch).
                                Never implies a final "net" off indicative charges. */}
                            <td className="px-3 py-2 text-xs">
                              {(() => {
                                const _indicative = order.charges_indicative === true;
                                return (
                                  <Pnl
                                    value={fill.realized_pnl}
                                    grossLabel={fill.brokerage == null || _indicative}
                                    indicative={fill.brokerage != null && _indicative}
                                  />
                                );
                              })()}
                            </td>
                            <td className="px-3 py-2 text-xs text-fg-2">{fill.status ?? '—'}</td>
                            <td className="px-3 py-2 font-mono text-[11px] tabular-nums text-fg-2">
                              {formatDateTimeIST(ts) ?? '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>

          {/* -- Order timeline (#10474 event log; #118 column spine as fallback) -- */}
          <section>
            <SectionHead title="Order timeline" />
            <Card>
              <LifecycleTimeline order={order} events={timelineEvents} loading={timelineLoading} error={timelineError} />
            </Card>
          </section>

          {/* -- Price derivation (algo-entry-null-limit-price D2) -- SEBI-audit provenance: the
              full 6-column stamp (price_source/reference_price/reference_ts/aggression_offset/
              band_clamped/original_order_type) written by the OMS entry-deriver (R1/R2) and the
              pre-existing exit converter — surfaced here so a human, not just SQL, can read WHY a
              price is what it is. Always rendered (never conditionally hidden) so a genuinely
              manually-typed order's own honest "Price: Manual"/"—" state is itself visible, not
              silently absent (mirrors OrdersTable's leg-cell badge, which is likewise never blank
              now).
              !! Stage-4 QA A/B loop-back (iter-2 BLOCKER 2) — the "Price source" badge label reuses
              !! the SAME PRICE_SOURCE_BADGE map as OrdersTable.jsx (never bare "Manual" — see
              !! tradingTransforms.js). The "Reference as of" field below was renamed "Priced as of"
              !! because it collided with the PRE-EXISTING "as of <timestamp>" aside on the Broker
              !! Cross-Check section heading further down (:461-ish, untouched) — two "as of" labels
              !! in the same drawer were not disambiguable at a glance (or by the pre-existing
              !! `getByText(/as of/i)` test query). "Priced as of" reads as "the reference price used
              !! for derivation was captured at this time", distinct from the broker section's
              !! "this reconciliation snapshot was fetched at this time". */}
          <section>
            <SectionHead title="Price Derivation" />
            <Card>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <Field label="Price source" full>
                  {(() => {
                    const priceBadge = getPriceSourceBadge(order.price_source, order.band_clamped);
                    return (
                      <span className="flex items-center gap-1.5">
                        <Badge variant={priceBadge.variant} size="sm">{priceBadge.label}</Badge>
                        {/* Raw wire value alongside the human label — entry-vs-exit is
                            distinguishable by the `entry_*` prefix even when the badge itself
                            reads "Band-clamped" (SEBI audit). */}
                        {order.price_source && (
                          <span className="text-[10px] font-mono text-fg-2">({order.price_source})</span>
                        )}
                      </span>
                    );
                  })()}
                </Field>
                <Field label="Reference price" mono>{formatPrice(order.reference_price)}</Field>
                <Field label="Priced as of" mono>{formatDateTimeIST(order.reference_ts) ?? '—'}</Field>
                <Field label="Applied offset" mono>
                  {(() => {
                    const off = _num(order.aggression_offset);
                    if (off == null) return '—';
                    return `${off >= 0 ? '+' : ''}${formatPrice(off)}`;
                  })()}
                </Field>
                <Field label="Band clamped">
                  {order.band_clamped === true ? (
                    <Badge variant="warn" size="sm">Yes</Badge>
                  ) : order.band_clamped === false ? (
                    <span className="text-fg-2">No</span>
                  ) : '—'}
                </Field>
                <Field label="Placed as">
                  {/*! R4 defense-in-depth — a MARKET the broker doesn't support for this
                      instrument was silently rewritten to a priced LIMIT; surface that verbatim
                      (never let it look like the operator originally chose LIMIT). */}
                  {order.original_order_type === 'MARKET' ? (
                    <span
                      className="text-warning font-semibold"
                      title="Placed as MARKET — the broker doesn't support it for this instrument; converted to a priced LIMIT"
                    >
                      MARKET → {order.type ?? order.order_type ?? 'LIMIT'}
                    </span>
                  ) : (order.original_order_type ?? '—')}
                </Field>
              </dl>
            </Card>
          </section>

          {/* -- Broker cross-check (reconciliation / audit surface) -- */}
          <section>
            <SectionHead title="Broker Cross-Check" aside={brokerAsOfLabel ? `as of ${brokerAsOfLabel}` : 'Not yet reconciled'} />

            {/* Reconcile state — loud label (R104.5); shared DS component with readable text */}
            <div className="mb-3">
              <ReconcileBadge state={reconcileState} showLabel />
            </div>

            {brokerRecord ? (
              <Card>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                  <Field label="Account" mono>{brokerRecord.broker_client_id}</Field>
                  <Field label="Broker Order ID" mono>{brokerRecord.broker_order_id}</Field>
                </dl>

                {/* DeeTrade vs broker diff — mismatches highlighted */}
                <div className="mt-4 border-t border-[var(--divider)] pt-3">
                  <div className="grid grid-cols-[1fr_auto_auto] items-center gap-x-4 gap-y-1.5 text-sm">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-fg-3" />
                    <span className="text-right text-[10px] font-semibold uppercase tracking-wider text-fg-3">DeeTrade</span>
                    <span className="text-right text-[10px] font-semibold uppercase tracking-wider text-fg-3">Broker</span>
                    {diffRows.map((r) => {
                      const bad = mismatched(r);
                      const fmt = (v) => (v == null ? '—' : r.price ? formatPrice(v) : String(v));
                      const cls = bad ? 'text-loss font-semibold' : 'text-fg-1';
                      return (
                        <React.Fragment key={r.label}>
                          <span className="text-xs text-fg-2">{r.label}</span>
                          <span className={`text-right font-mono text-xs tabular-nums ${cls}`}>{fmt(r.dt)}</span>
                          <span className={`text-right font-mono text-xs tabular-nums ${cls}`}>{fmt(r.bk)}</span>
                        </React.Fragment>
                      );
                    })}
                  </div>
                  {diffRows.some(mismatched) && (
                    <p className="mt-2 text-[11px] text-loss">Highlighted fields differ between DeeTrade and the broker book.</p>
                  )}
                </div>
              </Card>
            ) : (
              <p className="text-sm text-fg-2">
                {pageReconcileSnapshot
                  ? 'No broker record found for this order in the last reconcile.'
                  : 'Page reconcile snapshot not available.'}
              </p>
            )}
          </section>
        </div>

        {/*>>> FOOTER <<<*/}
        {/* R104.6 — only the explicit button triggers onRefreshBroker; never on open */}
        <div className="border-t border-[var(--divider)] bg-bg-2 px-5 py-4">
          <Button
            variant="secondary"
            className="w-full"
            onClick={() => {
              console.log('[DIAG] OrderDetailDrawer: user triggered onRefreshBroker');
              onRefreshBroker?.();
            }}
          >
            Refresh broker data
          </Button>
          <p className="mt-2 text-center text-[10px] text-fg-3">
            Rate-limited — use sparingly. Data shown is from the page snapshot.
          </p>
        </div>
      </aside>
    </>
  );
};

export default OrderDetailDrawer;
